import { Button } from '@getro/rombo';
import { ChevronDown, ChevronRight } from 'lucide-react';
import React from 'react';
import { Box, Flex, Image, Text } from 'rebass/styled-components';
import PropTypes from 'prop-types';

import IconStars from '../../../assets/icon-stars.svg';
import { useAiFilters } from './useAiFilters';

export const AIFilters = ({ setFilters }) => {
  const { value, setValue, showInput, setShowInput, failure, isFetching, onEnter, inputRef } = useAiFilters({
    setFilters,
  });

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        borderRadius: '4px',
        position: 'relative',
        zIndex: 1,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: '4px',
          padding: '1px',
          background: 'linear-gradient(118deg, #FF9F8A -21.79%, #F4F 27.75%, #4900FF 112.28%)',
          mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          maskComposite: 'exclude',
          overflow: 'hidden',
        },
      }}
    >
      <Flex sx={{ width: '100%' }}>
        <Flex
          data-testid="filter-title-trigger"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            cursor: 'pointer',
            zIndex: 2,
            position: 'relative',
            p: '7px',
            m: '1px',
            borderRadius: '4px',
            ':hover': {
              bg: !showInput ? 'neutral.20' : 'transparent',
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            setShowInput(!showInput);

            setTimeout(() => {
              if (inputRef.current) {
                inputRef.current.selectionStart = inputRef.current.value.length;
                inputRef.current.selectionEnd = inputRef.current.value.length;
              }
            }, 0);
          }}
        >
          <Flex width="100%" sx={{ alignItems: 'center', gap: '8px' }}>
            <Image src={IconStars} width="16px" height="16px" alt="Stars" />
            <Text flexGrow="1" as="p" fontSize="14px" fontWeight="500" lineHeight="1.4" color="text.dark">
              Start filtering with AI
            </Text>
            <Box as={showInput ? ChevronDown : ChevronRight} size="16px" sx={{ color: 'text.subtle' }} />
          </Flex>
        </Flex>
      </Flex>
      {showInput && (
        <Flex flexDirection="column" px="8px" pb="8px" sx={{ gap: '8px', position: 'relative' }}>
          <Text fontSize="14px" lineHeight="1.4" color="text.subtle">
            Describe what you need and let our AI set the right filters.
          </Text>
          <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
            <Flex
              ref={inputRef}
              placeholder="Example: Product managers working in the AI department"
              as="textarea"
              value={value}
              autoFocus
              data-testid="search-with-ai-filter-input"
              onChange={({ target }) => {
                setValue(target.value);
                // Grow the textarea with its content. Written through the ref rather than the event
                // target, which is the same node, so the handler isn't mutating its own parameter.
                if (!inputRef.current) return;
                inputRef.current.style.height = target.value.length > 0 ? `${target.scrollHeight}px` : '56px';
              }}
              onKeyDown={({ target, key }) => {
                if (key === 'Enter' && target.value.trim()) {
                  onEnter();
                }
              }}
              wrap="soft"
              sx={{
                '&::placeholder': {
                  color: 'text.subtle',
                  fontSize: '14px',
                  lineHeight: '19.6px',
                },
                fontSize: '14px',
                padding: '8px',
                color: 'text.main',
                outline: 'none',
                borderColor: 'neutral.400',
                borderRadius: '4px',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'pre-wrap',
                height: '72px',
                minHeight: '72px',
                resize: 'none',
                overflow: 'hidden',
              }}
            />
          </Flex>

          <Button
            size="small"
            loading={isFetching}
            onClick={() => onEnter()}
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',

              '&:disabled:not(.ladda-button)': {
                cursor: 'not-allowed',
                bg: 'neutral.300',
              },
            }}
          >
            Search with AI
          </Button>
          {failure && (
            <Box data-testid="search-with-ai-error" sx={{ fontSize: '14px', lineHeight: '1.4' }}>
              <Text as="span" color="text.error">
                {failure.message}
              </Text>{' '}
              <Text as="span" color="text.subtle">
                {failure.hint}
              </Text>
            </Box>
          )}
        </Flex>
      )}
    </Flex>
  );
};

AIFilters.propTypes = {
  setFilters: PropTypes.func.isRequired,
};
